import React from 'react'
import { useDispatch } from 'react-redux'

import { setBreadcrumbs } from '../store/breadcrumbs'
import { setToolbar } from '../store/toolbar'

import API from '../api/fetchAPI'

import HomeIcon from '@mui/icons-material/HomeRounded'
import AdminIcon from '@mui/icons-material/AdminPanelSettingsRounded'
import WorkIcon from '@mui/icons-material/WorkRounded'

import Button from '@mui/material/Button'
import Chip from '@mui/material/Chip'
import CircularProgress from '@mui/material/CircularProgress'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogContentText from '@mui/material/DialogContentText'
import DialogTitle from '@mui/material/DialogTitle'
import IconButton from '@mui/material/IconButton'
import Tooltip from '@mui/material/Tooltip'

import CancelIcon from '@mui/icons-material/CancelRounded'
import DeleteIcon from '@mui/icons-material/DeleteForeverRounded'
import StopIcon from '@mui/icons-material/StopCircleRounded'
import RefreshIcon from '@mui/icons-material/RefreshRounded'

import './JobManagement.scss'


const POLL_INTERVAL_MS = 5000


function formatDuration(isoStart) {
    const seconds = Math.floor((Date.now() - new Date(isoStart).getTime()) / 1000)
    if (seconds < 60) return `${seconds}s`
    const minutes = Math.floor(seconds / 60)
    const remainingSeconds = seconds % 60
    if (minutes < 60) return `${minutes}m ${remainingSeconds}s`
    const hours = Math.floor(minutes / 60)
    const remainingMinutes = minutes % 60
    return `${hours}h ${remainingMinutes}m`
}


function ConfirmDialog({ open, title, message, confirmLabel, confirmColor, onConfirm, onClose }) {
    return (
        <Dialog open={open} onClose={onClose}>
            <DialogTitle>{title}</DialogTitle>
            <DialogContent>
                <DialogContentText>{message}</DialogContentText>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Abort</Button>
                <Button color={confirmColor ?? 'error'} variant="contained" onClick={onConfirm}>
                    {confirmLabel}
                </Button>
            </DialogActions>
        </Dialog>
    )
}


export default function JobManagement() {

    const dispatch = useDispatch()

    const [jobs, setJobs] = React.useState([])
    const [loading, setLoading] = React.useState(true)
    const [error, setError] = React.useState(null)
    const [pending, setPending] = React.useState(null)
    const [tick, setTick] = React.useState(0)

    const [dialog, setDialog] = React.useState(null)

    React.useEffect(() => {
        dispatch(setBreadcrumbs([
            { key: 'home',  label: 'Home',  path: '/home',  icon: HomeIcon  },
            { key: 'admin', label: 'Admin', path: '/admin', icon: AdminIcon },
            { key: 'jobs',  label: 'Job Management', path: '/admin/jobs', icon: WorkIcon }
        ]))
        dispatch(setToolbar(null))
    }, [])

    React.useEffect(() => {
        let cancelled = false
        setLoading(true)
        API.get('/api/import/admin/jobs')
            .then(res => { if (!cancelled) { setJobs(res.jobs ?? []); setError(null) } })
            .catch(() => { if (!cancelled) setError('Could not load jobs') })
            .finally(() => { if (!cancelled) setLoading(false) })
        return () => { cancelled = true }
    }, [tick])

    React.useEffect(() => {
        const id = setInterval(() => setTick(t => t + 1), POLL_INTERVAL_MS)
        return () => clearInterval(id)
    }, [])

    const doAction = (endpoint, importId) => {
        setDialog(null)
        setPending(importId)
        API.post(endpoint, { params: { importId } })
            .then(() => setTick(t => t + 1))
            .catch(() => setError(`Action failed for ${importId}`))
            .finally(() => setPending(null))
    }

    const confirmCancel = (job) => setDialog({
        title: 'Cancel Job',
        message: <>Set import <strong>{job.name}</strong> to cancelled? The worker will stop at the next row boundary.</>,
        confirmLabel: 'Cancel Job',
        confirmColor: 'warning',
        onConfirm: () => doAction('/api/import/admin/jobs/cancel', job.importId)
    })

    const confirmKill = (job) => setDialog({
        title: 'Kill Job',
        message: <>Force-kill the worker for <strong>{job.name}</strong>? The thread will be terminated immediately.</>,
        confirmLabel: 'Kill',
        confirmColor: 'error',
        onConfirm: () => doAction('/api/import/admin/jobs/kill', job.importId)
    })

    const confirmRemove = (job) => setDialog({
        title: 'Remove Import',
        message: <>Permanently delete import <strong>{job.name}</strong> from the database? This cannot be undone.</>,
        confirmLabel: 'Delete',
        confirmColor: 'error',
        onConfirm: () => doAction('/api/import/admin/jobs/remove', job.importId)
    })

    return (
        <div className="page-content job-management">
            <div className="header-row">
                <h2>Job Management</h2>
                <Tooltip title="Refresh">
                    <IconButton onClick={() => setTick(t => t + 1)} size="small">
                        <RefreshIcon/>
                    </IconButton>
                </Tooltip>
            </div>

            {error && <div className="error-message">{error}</div>}

            {loading && jobs.length === 0
                ? <div className="spinner-wrap"><CircularProgress size={32}/></div>
                : jobs.length === 0
                    ? <div className="empty-state">No import jobs are currently running.</div>
                    : (
                        <table className="jobs-table">
                            <thead>
                                <tr>
                                    <th>Name</th>
                                    <th>File</th>
                                    <th>Import ID</th>
                                    <th>Started</th>
                                    <th>Running for</th>
                                    <th>Worker</th>
                                    <th></th>
                                </tr>
                            </thead>
                            <tbody>
                                {jobs.map(job => (
                                    <tr key={job.importId}>
                                        <td>{job.name}</td>
                                        <td>{job.fileName}</td>
                                        <td className="monospace">{job.importId}</td>
                                        <td>{job.createdAt ? new Date(job.createdAt).toLocaleString() : '—'}</td>
                                        <td>
                                            <Chip
                                                size="small"
                                                color="primary"
                                                label={job.createdAt ? formatDuration(job.createdAt) : '—'}
                                                icon={<CircularProgress size={12} style={{ color: 'white' }}/>}
                                            />
                                        </td>
                                        <td>
                                            <Chip
                                                size="small"
                                                color={job.hasWorker ? 'success' : 'default'}
                                                label={job.hasWorker ? 'active' : 'orphaned'}
                                            />
                                        </td>
                                        <td className="actions">
                                            <Tooltip title="Cancel (cooperative — stops at next row)">
                                                <span>
                                                    <IconButton
                                                        color="warning"
                                                        size="small"
                                                        disabled={pending === job.importId}
                                                        onClick={() => confirmCancel(job)}
                                                    >
                                                        <CancelIcon/>
                                                    </IconButton>
                                                </span>
                                            </Tooltip>
                                            <Tooltip title="Kill (force-terminate worker thread)">
                                                <span>
                                                    <IconButton
                                                        color="error"
                                                        size="small"
                                                        disabled={pending === job.importId || !job.hasWorker}
                                                        onClick={() => confirmKill(job)}
                                                    >
                                                        {pending === job.importId
                                                            ? <CircularProgress size={18}/>
                                                            : <StopIcon/>
                                                        }
                                                    </IconButton>
                                                </span>
                                            </Tooltip>
                                            <Tooltip title="Remove import from database">
                                                <span>
                                                    <IconButton
                                                        color="error"
                                                        size="small"
                                                        disabled={pending === job.importId}
                                                        onClick={() => confirmRemove(job)}
                                                    >
                                                        <DeleteIcon/>
                                                    </IconButton>
                                                </span>
                                            </Tooltip>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )
            }

            <ConfirmDialog
                open={dialog != null}
                title={dialog?.title}
                message={dialog?.message}
                confirmLabel={dialog?.confirmLabel}
                confirmColor={dialog?.confirmColor}
                onConfirm={dialog?.onConfirm}
                onClose={() => setDialog(null)}
            />
        </div>
    )
}
